import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { getAdminPacks, createAdminPack, updateAdminPack, deleteAdminPack } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';

export default function AdminPacks() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPack, setEditPack] = useState(null);
  const [form, setForm] = useState({ name: '', points: '', price_gbp: '', bonus_pct: '0', enabled: true, popular: false });

  useEffect(() => { if (authLoading) return; if (!user || !isAdmin) { navigate('/admin/login'); return; } loadPacks(); }, [user, isAdmin, navigate, authLoading]);
  const loadPacks = async () => { try { const res = await getAdminPacks(); setPacks(res.data.packs || []); } catch (e) { console.error(e); } finally { setLoading(false); } };
  const openCreate = () => { setEditPack(null); setForm({ name: '', points: '', price_gbp: '', bonus_pct: '0', enabled: true, popular: false }); setDialogOpen(true); };
  const openEdit = (p) => { setEditPack(p); setForm({ name: p.name, points: p.points, price_gbp: p.price_gbp, bonus_pct: p.bonus_pct || 0, enabled: p.enabled, popular: p.popular || false }); setDialogOpen(true); };
  const handleSubmit = async () => { const payload = { ...form, points: parseInt(form.points), price_gbp: parseFloat(form.price_gbp), bonus_pct: parseInt(form.bonus_pct) }; try { if (editPack) { await updateAdminPack(editPack._id, payload); toast.success(t('toastUpdated')); } else { await createAdminPack(payload); toast.success(t('toastCreated')); } setDialogOpen(false); loadPacks(); } catch { toast.error(t('toastFailed')); } };
  const handleDelete = async (id) => { if (!window.confirm(t('confirmDeleteShort'))) return; try { await deleteAdminPack(id); toast.success(t('toastDeleted')); loadPacks(); } catch { toast.error(t('toastFailed')); } };

  // Quick toggle enabled/disabled without opening the edit dialog
  const handleToggleEnabled = async (pack) => {
    try {
      await updateAdminPack(pack._id, { enabled: !pack.enabled });
      toast.success(pack.enabled ? (t('packDisabled') || 'Pack désactivé') : (t('packEnabled') || 'Pack activé'));
      loadPacks();
    } catch {
      toast.error(t('toastFailed'));
    }
  };

  if (!user || !isAdmin) return null;

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8">
        <div className="flex items-center justify-between mb-8">
          <div><h1 className="font-display text-3xl font-bold tracking-tight">{t('pointPacks')}</h1><p className="text-muted-foreground mt-1 text-sm">{t('managePacksDesc')}</p></div>
          <Button className="bg-primary text-primary-foreground btn-press gap-2 rounded-xl" onClick={openCreate}><Plus className="w-4 h-4" /> {t('newPack')}</Button>
        </div>
        <Card className="bg-white border-border" data-testid="admin-packs-table"><CardContent className="p-0">
          <Table><TableHeader><TableRow><TableHead>{t('name')}</TableHead><TableHead>{t('points')}</TableHead><TableHead>{t('price')}</TableHead><TableHead>{t('bonus')}</TableHead><TableHead>{t('status')}</TableHead><TableHead>{t('popular')}</TableHead><TableHead className="text-right">{t('actions')}</TableHead></TableRow></TableHeader>
            <TableBody>{packs.map((p) => (
              <TableRow key={p._id}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="font-mono">{p.points}</TableCell>
                <TableCell className="font-mono">£{p.price_gbp}</TableCell>
                <TableCell>{p.bonus_pct > 0 ? <Badge variant="outline" className="border-green-600/40 text-green-700 text-xs">+{p.bonus_pct}%</Badge> : '-'}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={p.enabled}
                      onCheckedChange={() => handleToggleEnabled(p)}
                      data-testid={`pack-toggle-${p._id}`}
                    />
                    <Badge variant={p.enabled ? 'default' : 'secondary'} className={p.enabled ? 'bg-green-100 text-green-700 border-green-200' : 'bg-stone-200 text-stone-600'}>
                      {p.enabled ? t('active') : t('disabled')}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell>{p.popular ? <Badge className="bg-primary/10 text-primary border-primary/20">{t('popular')}</Badge> : '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(p._id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </CardContent></Card>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}><DialogContent className="bg-white border-border"><DialogHeader><DialogTitle className="font-display text-xl">{editPack ? t('editPack') : t('newPack')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>{t('name')}</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label>{t('points')}</Label><Input type="number" value={form.points} onChange={(e) => setForm({...form, points: e.target.value})} className="rounded-xl" /></div><div className="space-y-2"><Label>{t('price')} (£)</Label><Input type="number" value={form.price_gbp} onChange={(e) => setForm({...form, price_gbp: e.target.value})} className="rounded-xl" /></div></div>
            <div className="space-y-2"><Label>{t('bonusPct')}</Label><Input type="number" value={form.bonus_pct} onChange={(e) => setForm({...form, bonus_pct: e.target.value})} className="rounded-xl" /></div>
            <div className="flex items-center gap-4"><div className="flex items-center gap-2"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({...form, enabled: v})} /><Label>{t('enabled')}</Label></div><div className="flex items-center gap-2"><Switch checked={form.popular} onCheckedChange={(v) => setForm({...form, popular: v})} /><Label>{t('popular')}</Label></div></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">{t('cancel')}</Button><Button className="bg-primary text-primary-foreground btn-press rounded-xl" onClick={handleSubmit}>{editPack ? t('update') : t('create')}</Button></DialogFooter>
        </DialogContent></Dialog>
      </main>
    </div>
  );
}
